/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useCompany } from "@/hooks/useCompany";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, Search, Edit2, Trash2, Users, FileText, ChevronLeft, ChevronRight, Loader2,
} from "lucide-react";

const tenantDb = supabase as any;

const LANG_LABELS: Record<string, string> = {
  ar: "العربية",
  en: "English",
  fr: "Français",
  all: "Global",
};

const LANG_COLORS: Record<string, string> = {
  ar: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
  en: "bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-400",
  fr: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400",
  all: "bg-muted text-muted-foreground",
};

interface AuthorForm {
  name: string;
  language: string;
  bio: string;
}

const emptyForm: AuthorForm = { name: "", language: "en", bio: "" };

export default function AuthorsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { currentCompanyId } = useCompany();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get("page") || "1");
  const perPage = 25;
  const search = searchParams.get("q") || "";
  const filterLang = searchParams.get("lang") || "all";

  const setParam = useCallback((key: string, value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (!value || value === "all" || (key === "page" && value === "1")) next.delete(key);
      else next.set(key, value);
      if (key !== "page") next.delete("page");
      return next;
    });
  }, [setSearchParams]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<AuthorForm>(emptyForm);

  const { data: queryResult, isLoading } = useQuery({
    queryKey: ["authors", currentCompanyId, page, search, filterLang],
    queryFn: async () => {
      let query = tenantDb.from("authors").select("*", { count: "exact" }).eq("company_id", currentCompanyId);
      if (search) query = query.ilike("name", `%${search}%`);
      if (filterLang !== "all") query = query.eq("language", filterLang);
      const from = (page - 1) * perPage;
      const { data, count, error } = await query.order("name").range(from, from + perPage - 1);
      if (error) throw error;
      return { authors: data || [], total: count || 0 };
    },
    enabled: !!user && !!currentCompanyId,
  });

  const authors = queryResult?.authors || [];
  const totalCount = queryResult?.total || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / perPage));

  const authorIds = authors.map((a: any) => a.id);
  const { data: articleCounts = {} } = useQuery({
    queryKey: ["author-article-counts", authorIds],
    queryFn: async () => {
      if (authorIds.length === 0) return {};
      const { data } = await tenantDb.from("articles").select("editorial_author_id").eq("company_id", currentCompanyId).in("editorial_author_id", authorIds);
      const counts: Record<string, number> = {};
      (data || []).forEach((a: any) => { counts[a.editorial_author_id] = (counts[a.editorial_author_id] || 0) + 1; });
      return counts;
    },
    enabled: authorIds.length > 0 && !!currentCompanyId,
  });

  const saveMutation = useMutation({
    mutationFn: async (data: AuthorForm & { id?: string }) => {
      if (data.id) {
        const { error } = await tenantDb.from("authors").update({
          name: data.name, language: data.language, bio: data.bio || null, avatar_url: null,
        }).eq("id", data.id).eq("company_id", currentCompanyId);
        if (error) throw error;
      } else {
        const { error } = await tenantDb.from("authors").insert({
          company_id: currentCompanyId, name: data.name, language: data.language, bio: data.bio || null, avatar_url: null,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["authors"] });
      setDialogOpen(false);
      setEditingId(null);
      setForm(emptyForm);
      toast({ title: editingId ? "Author updated" : "Author created" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await tenantDb.from("authors").delete().eq("id", id).eq("company_id", currentCompanyId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["authors"] });
      toast({ title: "Author deleted" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const openEdit = (author: any) => {
    setEditingId(author.id);
    setForm({ name: author.name, language: author.language, bio: author.bio || "" });
    setDialogOpen(true);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const initials = (name: string) => name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Authors</h1>
          <p className="text-sm text-muted-foreground">{totalCount} author{totalCount !== 1 ? "s" : ""}</p>
        </div>
        <Button size="sm" onClick={openCreate}><Plus className="h-4 w-4 mr-1" /> New Author</Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search authors..." value={search} onChange={(e) => setParam("q", e.target.value)} className="pl-9" />
        </div>
        <Select value={filterLang} onValueChange={(v) => setParam("lang", v)}>
          <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Languages</SelectItem>
            <SelectItem value="ar">العربية</SelectItem>
            <SelectItem value="en">English</SelectItem>
            <SelectItem value="fr">Français</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-14 w-full rounded" />)}</div>
      ) : authors.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Users className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground">No authors found</h3>
          <p className="text-sm text-muted-foreground mt-1">Adjust your filters or create a new author.</p>
          <Button className="mt-4" onClick={openCreate}><Plus className="mr-2 h-4 w-4" /> Add Author</Button>
        </div>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30">
                <TableHead>Author</TableHead>
                <TableHead>Language</TableHead>
                <TableHead className="hidden md:table-cell">Bio</TableHead>
                <TableHead className="text-center">Articles</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {authors.map((author: any) => (
                <TableRow key={author.id} className="group hover:bg-muted/60 transition-colors">
                  <TableCell className="py-2">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-9 w-9">
                        <AvatarFallback className="text-xs font-medium">{initials(author.name)}</AvatarFallback>
                      </Avatar>
                      <Link to={`/authors/${author.id}/articles`} className="font-medium text-foreground hover:text-primary transition-colors">{author.name}</Link>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={`${LANG_COLORS[author.language] || LANG_COLORS.all} text-[11px] border-transparent`}>{LANG_LABELS[author.language] || author.language}</Badge>
                  </TableCell>
                  <TableCell className="hidden md:table-cell max-w-[300px]">
                    <span className="text-sm text-muted-foreground line-clamp-1">{author.bio || "—"}</span>
                  </TableCell>
                  <TableCell className="text-center">
                    <Link to={`/authors/${author.id}/articles`} className="text-sm text-primary hover:underline">{(articleCounts as any)[author.id] || 0}</Link>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <IconButton variant="ghost" className="h-7 w-7" onClick={() => openEdit(author)} label={t("actions.edit")}><Edit2 className="h-3.5 w-3.5" /></IconButton>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <IconButton variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" label={t("actions.delete")}><Trash2 className="h-3.5 w-3.5" /></IconButton>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this author?</AlertDialogTitle>
                            <AlertDialogDescription>"{author.name}" will be deleted. Linked articles will not be removed.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteMutation.mutate(author.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-muted-foreground">Page {page} / {totalPages}</p>
          <div className="flex items-center gap-1">
            <IconButton variant="outline" className="h-8 w-8" disabled={page <= 1} onClick={() => setParam("page", String(page - 1))} label={t("actions.previousPage")}><ChevronLeft className="h-4 w-4" /></IconButton>
            <IconButton variant="outline" className="h-8 w-8" disabled={page >= totalPages} onClick={() => setParam("page", String(page + 1))} label={t("actions.nextPage")}><ChevronRight className="h-4 w-4" /></IconButton>
          </div>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Author" : "New Author"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium">Full Name</label>
              <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="John Doe" />
            </div>
            <div>
              <label className="text-sm font-medium">Language</label>
              <Select value={form.language} onValueChange={(v) => setForm(f => ({ ...f, language: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ar">العربية</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="fr">Français</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Biography</label>
              <Textarea value={form.bio} onChange={(e) => setForm(f => ({ ...f, bio: e.target.value }))} placeholder="Short biography..." rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={() => saveMutation.mutate({ ...form, id: editingId || undefined })} disabled={!form.name || saveMutation.isPending}>
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              {editingId ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
